import { useState, useEffect } from "react";
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  Clock,
  Plus,
  Trash2,
  Check,
  X,
  Ban
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarPlanner = ({ token }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingBlocks, setEditingBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCalendarMonth();
  }, [currentMonth]);

  const fetchCalendarMonth = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/owner/calendar/month/${currentMonth.year}/${currentMonth.month}`,
        { headers }
      );
      setCalendarData(res.data);
    } catch (err) {
      console.error("Error fetching calendar:", err);
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 1) {
        return { year: prev.year - 1, month: 12 };
      }
      return { ...prev, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 12) {
        return { year: prev.year + 1, month: 1 };
      }
      return { ...prev, month: prev.month + 1 };
    });
  };

  const selectDate = (day) => {
    setSelectedDate(day);
    // Initialize blocks for editing
    if (day.blocks && day.blocks.length > 0) {
      setEditingBlocks([...day.blocks]);
    } else {
      setEditingBlocks([{ start_time: "09:00", end_time: "17:00" }]);
    }
  };

  const addBlock = () => {
    setEditingBlocks(prev => [...prev, { start_time: "09:00", end_time: "17:00" }]);
  };

  const removeBlock = (index) => {
    setEditingBlocks(prev => prev.filter((_, i) => i !== index));
  };

  const updateBlock = (index, field, value) => {
    setEditingBlocks(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const saveAvailability = async (available = true) => {
    if (!selectedDate) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/owner/calendar/date-availability`, {
        date: selectedDate.date,
        available,
        blocks: available ? editingBlocks : [],
        notes: ""
      }, { headers });
      
      toast.success(`Availability updated for ${selectedDate.date}`);
      fetchCalendarMonth();
      setSelectedDate(null);
    } catch (err) {
      toast.error("Failed to save availability");
    } finally {
      setSaving(false);
    }
  };

  const blockDate = async () => {
    if (!selectedDate) return;
    
    try {
      await axios.post(`${API}/owner/calendar/blocked-dates`, {
        date: selectedDate.date,
        reason: "Blocked by owner",
        all_day: true
      }, { headers });
      
      toast.success(`${selectedDate.date} blocked`);
      fetchCalendarMonth();
      setSelectedDate(null);
    } catch (err) {
      toast.error("Failed to block date");
    }
  };

  const unblockDate = async () => {
    if (!selectedDate || !selectedDate.is_blocked) return;
    
    // Find the blocked date ID
    try {
      const blockedRes = await axios.get(`${API}/owner/calendar/blocked-dates`, { headers });
      const blocked = blockedRes.data.blocked_dates?.find(b => b.date === selectedDate.date);
      
      if (blocked) {
        await axios.delete(`${API}/owner/calendar/blocked-dates/${blocked.id}`, { headers });
        toast.success(`${selectedDate.date} unblocked`);
        fetchCalendarMonth();
        setSelectedDate(null);
      }
    } catch (err) {
      toast.error("Failed to unblock date");
    }
  };

  const resetToDefault = async () => {
    if (!selectedDate) return;
    
    try {
      await axios.delete(`${API}/owner/calendar/date-availability/${selectedDate.date}`, { headers });
      toast.success("Reset to default schedule");
      fetchCalendarMonth();
      setSelectedDate(null);
    } catch (err) {
      // May fail if no custom availability exists - that's ok
      setSelectedDate(null);
    }
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate first day offset for proper grid alignment
  const firstDayOffset = calendarData?.days?.[0]
    ? dayNames.indexOf(calendarData.days[0].day_of_week.charAt(0).toUpperCase() + calendarData.days[0].day_of_week.slice(1, 3))
    : 0;

  return (
    <div className="space-y-6" data-testid="calendar-planner">
      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={prevMonth}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-xl font-bold text-gray-900">
          {monthNames[currentMonth.month - 1]} {currentMonth.year}
        </h2>
        <Button variant="outline" onClick={nextMonth}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gray-100 border border-gray-300"></div>
          <span>Unavailable</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
          <span>Blocked</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-100 border border-blue-300"></div>
          <span>Has Bookings</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-purple-100 border border-purple-300"></div>
          <span>Custom Schedule</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b">
          {dayNames.map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-600 bg-gray-50">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {/* Empty cells for offset */}
          {Array.from({ length: firstDayOffset }).map((_, i) => (
            <div key={`empty-${i}`} className="p-2 min-h-[80px] bg-gray-50/50 border-b border-r"></div>
          ))}
          
          {calendarData?.days?.map((day) => {
            const hasBookings = day.bookings && day.bookings.length > 0;
            const isToday = day.date === new Date().toISOString().split('T')[0];
            
            let bgColor = "bg-gray-50";
            let borderColor = "border-gray-200";
            
            if (day.is_blocked) {
              bgColor = "bg-red-50";
              borderColor = "border-red-200";
            } else if (hasBookings) {
              bgColor = "bg-blue-50";
              borderColor = "border-blue-200";
            } else if (day.has_custom_availability) {
              bgColor = day.available ? "bg-purple-50" : "bg-gray-100";
              borderColor = "border-purple-200";
            } else if (day.available) {
              bgColor = "bg-green-50";
              borderColor = "border-green-200";
            }
            
            return (
              <div
                key={day.date}
                className={`p-2 min-h-[80px] border-b border-r cursor-pointer hover:bg-opacity-80 transition-colors ${bgColor} ${
                  isToday ? "ring-2 ring-primary ring-inset" : ""
                }`}
                onClick={() => selectDate(day)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${isToday ? "text-primary" : "text-gray-700"}`}>
                    {day.day}
                  </span>
                  <div className="flex gap-1">
                    {day.is_blocked && <Ban className="w-3 h-3 text-red-500" />}
                    {day.has_custom_availability && <Clock className="w-3 h-3 text-purple-500" />}
                    {hasBookings && (
                      <span className="text-xs bg-blue-500 text-white px-1 rounded">
                        {day.bookings.length}
                      </span>
                    )}
                  </div>
                </div>
                
                {day.available && day.blocks && day.blocks.length > 0 && (
                  <div className="space-y-0.5">
                    {day.blocks.slice(0, 2).map((block, idx) => (
                      <div key={idx} className="text-xs text-gray-500 truncate">
                        {block.start_time}-{block.end_time}
                      </div>
                    ))}
                    {day.blocks.length > 2 && (
                      <div className="text-xs text-gray-400">+{day.blocks.length - 2} more</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Date Detail Panel */}
      {selectedDate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDate(null)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedDate.date}</h3>
                <p className="text-sm text-gray-500 capitalize">{selectedDate.day_of_week}</p>
              </div>
              <button onClick={() => setSelectedDate(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  selectedDate.is_blocked ? "bg-red-100 text-red-700" :
                  selectedDate.available ? "bg-green-100 text-green-700" :
                  "bg-gray-100 text-gray-700"
                }`}>
                  {selectedDate.is_blocked ? "Blocked" : selectedDate.available ? "Available" : "Unavailable"}
                </span>
                {selectedDate.has_custom_availability && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                    Custom Schedule
                  </span>
                )}
              </div>

              {/* Bookings */}
              {selectedDate.bookings && selectedDate.bookings.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Bookings</Label>
                  <div className="mt-2 space-y-2">
                    {selectedDate.bookings.map((booking, idx) => (
                      <div key={idx} className="p-2 bg-blue-50 rounded-lg text-sm">
                        <p className="font-medium">{booking.name || booking.customer_name}</p>
                        <p className="text-gray-500">{booking.booking_time}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Availability Blocks Editor */}
              {!selectedDate.is_blocked && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Availability Blocks</Label>
                  <div className="mt-2 space-y-2">
                    {editingBlocks.map((block, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={block.start_time}
                          onChange={(e) => updateBlock(idx, "start_time", e.target.value)}
                          className="w-28"
                        />
                        <span className="text-gray-400">to</span>
                        <Input
                          type="time"
                          value={block.end_time}
                          onChange={(e) => updateBlock(idx, "end_time", e.target.value)}
                          className="w-28"
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeBlock(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addBlock} className="w-full">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Time Block
                    </Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2 pt-4 border-t">
                {!selectedDate.is_blocked && (
                  <>
                    <Button 
                      onClick={() => saveAvailability(true)} 
                      disabled={saving}
                      className="w-full"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Save as Available
                    </Button>
                    <Button 
                      onClick={() => saveAvailability(false)} 
                      disabled={saving}
                      variant="outline"
                      className="w-full"
                    >
                      Mark as Unavailable
                    </Button>
                    <Button 
                      onClick={blockDate}
                      variant="outline"
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Block This Date
                    </Button>
                  </>
                )}
                
                {selectedDate.is_blocked && (
                  <Button 
                    onClick={unblockDate}
                    className="w-full"
                  >
                    Unblock This Date
                  </Button>
                )}
                
                {selectedDate.has_custom_availability && (
                  <Button 
                    onClick={resetToDefault}
                    variant="outline"
                    className="w-full"
                  >
                    Reset to Default Schedule
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPlanner;
